import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Container, 
  Paper, 
  Grid,
  Snackbar,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
  ThemeProvider,
  createTheme,
  InputAdornment,
  Card,
  CardContent,
  Avatar
} from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneIcon from '@mui/icons-material/Phone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailIcon from '@mui/icons-material/Email';
import SubjectIcon from '@mui/icons-material/Subject';
import CategoryIcon from '@mui/icons-material/Category';
import MessageIcon from '@mui/icons-material/Message';
import SendIcon from '@mui/icons-material/Send';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';

// Create a custom theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#6200EA', // Deep purple
      light: '#B388FF',
      dark: '#4A148C',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#00BFA5', // Teal
      light: '#64FFDA',
      dark: '#008E76',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
    text: {
      primary: '#212121',
      secondary: '#616161',
    },
    error: {
      main: '#FF5252',
    },
    success: {
      main: '#00C853',
    }
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h3: {
      fontWeight: 700,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 500,
    },
    h6: {
      fontWeight: 500,
    }
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          }
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          boxShadow: '0 4px 10px rgba(98, 0, 234, 0.2)',
          '&:hover': {
            boxShadow: '0 6px 12px rgba(98, 0, 234, 0.3)',
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        }
      }
    }
  }
});

export default function HelpPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    issueType: ''
  });
  
  const [errors, setErrors] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    if (!formData.issueType) newErrors.issueType = 'Please select an issue type';
    
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    console.log("Form submitted:", formData);
    
    setFormData({
      name: '',
      email: '',
      subject: '',
      message: '',
      issueType: ''
    });
    
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  return (
    <ThemeProvider theme={theme}>
        <Container maxWidth="lg">
          <Paper 
            sx={{ 
              p: { xs: 3, md: 5 }, 
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            {/* Background decoration */}
            <Box sx={{
              position: 'absolute',
              width: '600px',
              height: '600px',
              borderRadius: '50%',
              top: '-300px',
              right: '-300px',
              zIndex: 0
            }} />
            
            <Box sx={{
              position: 'absolute',
              width: '400px',
              height: '400px',
              borderRadius: '50%',
              bottom: '-200px',
              left: '-200px',
              zIndex: 0
            }} />
            
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography 
                variant="h3" 
                sx={{ 
                  mb: 4, 
                  textAlign: 'center', 
                  fontWeight: 'bold', 
                  background: 'linear-gradient(45deg, #6200EA 30%, #00BFA5 90%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                How Can We Help You?
              </Typography>
              
              <Grid container spacing={4} mb={4} justifyContent='center'>
                {/* Contact Information */}
                <Grid item xs={12} md={4}>
                  <Card 
                    elevation={0} 
                    sx={{ 
                      height: '100%', 
                      background: 'linear-gradient(135deg, rgba(98, 0, 234, 0.04) 0%, rgba(98, 0, 234, 0.1) 100%)',
                      border: '1px solid rgba(98, 0, 234, 0.1)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: '0 10px 20px rgba(98, 0, 234, 0.15)'
                      }
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h5" sx={{ mb: 3, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <QuestionAnswerIcon /> Contact Us
                      </Typography>
                      <Divider sx={{ mb: 4, borderColor: 'rgba(98, 0, 234, 0.2)' }} />
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                        <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                          <PhoneIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Call us at
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            +1 (555) 123-4567
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                        <Avatar sx={{ bgcolor: '#25D366', mr: 2 }}>
                          <WhatsAppIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            WhatsApp
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            +1 (555) 987-6543
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                        <Avatar sx={{ bgcolor: 'secondary.main', mr: 2 }}>
                          <MailOutlineIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Email us at
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            support@company.com
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ 
                        mt: 4, 
                        p: 2, 
                        borderRadius: 2, 
                        bgcolor: 'rgba(0, 191, 165, 0.1)',
                        border: '1px solid rgba(0, 191, 165, 0.2)'
                      }}>
                        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                          Our support team is available Monday through Friday, 9am-5pm EST.
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                          For urgent matters outside business hours, please use our emergency hotline:
                          <Box component="span" sx={{ display: 'block', color: 'primary.main', fontWeight: 600, mt: 1 }}>
                            +1 (555) 911-0123
                          </Box>
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

                {/* Contact Form */}
                <Grid container spacing={0}>
                  <Grid item xs={12} md={8} width='100%'>
                    <Card 
                      elevation={0} 
                      sx={{ 
                        p: 3, 
                        border: '1px solid rgba(0, 0, 0, 0.08)',
                        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.05)'
                      }}
                    >
                      <Typography variant="h5" sx={{ mb: 4, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SendIcon /> Send Us a Message
                      </Typography>
                      
                      <form onSubmit={handleSubmit}>
                        <Grid container spacing={3} display='flex' flexDirection='column'>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Your Name"
                              name="name"
                              value={formData.name}
                              onChange={handleChange}
                              error={!!errors.name}
                              helperText={errors.name}
                              required
                              variant="outlined"
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <PersonOutlineIcon color="primary" />
                                  </InputAdornment>
                                ),
                              }}
                              sx={{ 
                                '& .MuiOutlinedInput-root': {
                                  '&.Mui-focused fieldset': {
                                    borderColor: 'primary.main',
                                    borderWidth: 2
                                  }
                                }
                              }}
                            />
                          </Grid>
                          
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Your Email"
                              name="email"
                              type="email"
                              value={formData.email}
                              onChange={handleChange}
                              error={!!errors.email}
                              helperText={errors.email}
                              required
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <EmailIcon color="primary" />
                                  </InputAdornment>
                                ),
                              }}
                              sx={{ 
                                '& .MuiOutlinedInput-root': {
                                  '&.Mui-focused fieldset': {
                                    borderColor: 'primary.main',
                                    borderWidth: 2
                                  }
                                }
                              }}
                            />
                          </Grid>
                          
                          <Grid item xs={12}>
                            <FormControl fullWidth error={!!errors.issueType}>
                              <InputLabel>Issue Type</InputLabel>
                              <Select
                                name="issueType"
                                value={formData.issueType}
                                onChange={handleChange}
                                label="Issue Type"
                                startAdornment={
                                  <InputAdornment position="start">
                                    <CategoryIcon color="primary" />
                                  </InputAdornment>
                                }
                                sx={{ 
                                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'primary.main',
                                    borderWidth: 2
                                  }
                                }}
                              >
                                <MenuItem value="technical">Technical Support</MenuItem>
                                <MenuItem value="billing">Billing Question</MenuItem>
                                <MenuItem value="feedback">Feedback</MenuItem>
                                <MenuItem value="other">Other</MenuItem>
                              </Select>
                              {errors.issueType && (
                                <Typography variant="caption" color="error">
                                  {errors.issueType}
                                </Typography>
                              )}
                            </FormControl>
                          </Grid>
                          
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Subject"
                              name="subject"
                              value={formData.subject}
                              onChange={handleChange}
                              error={!!errors.subject}
                              helperText={errors.subject}
                              required
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <SubjectIcon color="primary" />
                                  </InputAdornment>
                                ),
                              }}
                              sx={{ 
                                '& .MuiOutlinedInput-root': {
                                  '&.Mui-focused fieldset': {
                                    borderColor: 'primary.main',
                                    borderWidth: 2
                                  }
                                }
                              }}
                            />
                          </Grid>
                          
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Your Message"
                              name="message"
                              multiline
                              rows={4}
                              value={formData.message}
                              onChange={handleChange}
                              error={!!errors.message}
                              helperText={errors.message}
                              required
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 2, mr: 1 }}>
                                    <MessageIcon color="primary" />
                                  </InputAdornment>
                                ),
                              }}
                              sx={{ 
                                '& .MuiOutlinedInput-root': {
                                  '&.Mui-focused fieldset': {
                                    borderColor: 'primary.main',
                                    borderWidth: 2
                                  }
                                }
                              }}
                            />
                          </Grid>
                          
                          <Grid item xs={12}>
                            <Button 
                              type="submit" 
                              variant="contained" 
                              color="primary" 
                              size="large"
                              fullWidth
                              startIcon={<SendIcon />}
                              sx={{ 
                                py: 1.5,
                                mt: 1,
                                background: 'linear-gradient(45deg, #6200EA 30%, #B388FF 90%)'
                              }}
                            >
                              Send Message
                            </Button>
                          </Grid>
                        </Grid>
                      </form>
                    </Card>
                  </Grid>
                </Grid>
              
              {/* FAQ Section */}
              <Box sx={{ mt: 8 }}>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    mb: 4, 
                    textAlign: 'center', 
                    fontWeight: 700,
                    background: 'linear-gradient(45deg, #6200EA 30%, #00BFA5 90%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  Frequently Asked Questions
                </Typography>
                
                <Grid container spacing={3}>
                  {[
                    {
                      question: "How long does it take to get a response?",
                      answer: "We typically respond to all inquiries within 24-48 business hours."
                    },
                    {
                      question: "Do you offer refunds?",
                      answer: "Yes, we offer full refunds within 30 days of purchase if you're not satisfied with our service."
                    },
                    {
                      question: "How do I reset my password?",
                      answer: "You can reset your password by clicking on the 'Forgot Password' link on the login page."
                    },
                    {
                      question: "Can I change my subscription plan?",
                      answer: "Yes, you can upgrade or downgrade your subscription plan at any time through your account settings."
                    }
                  ].map((faq, index) => (
                    <Grid item xs={12} sm={6} key={index} width='100%'>
                      <Card 
                        elevation={0} 
                        sx={{ 
                          p: 3, 
                          height: '100%',
                          border: '1px solid rgba(0, 0, 0, 0.08)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            borderColor: 'primary.light',
                            boxShadow: '0 8px 20px rgba(98, 0, 234, 0.1)',
                            transform: 'translateY(-5px)'
                          }
                        }}
                      >
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            mb: 2, 
                            fontWeight: 600,
                            color: 'primary.main'
                          }}
                        >
                          {faq.question}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          {faq.answer}
                        </Typography>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>
          </Paper>
        </Container>
      
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity="success" 
          variant="filled"
          sx={{ 
            width: '100%',
            boxShadow: '0 4px 20px rgba(0, 200, 83, 0.3)'
          }}
        >
          Your message has been sent successfully! We'll get back to you soon.
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}